<script setup>
import * as Sentry from "@sentry/vue";
import { debounce } from "lodash";
import * as pdfjsLib from "pdfjs-dist";
import workerSrc from "pdfjs-dist/build/pdf.worker.min.mjs?worker&url";
import { onBeforeUnmount, onMounted, ref, toRefs, watch } from "vue";

import SkeletonLoader from "@/components/shared/loaders/BaseSkeletonLoader.vue";

pdfjsLib.GlobalWorkerOptions.workerSrc = workerSrc;

// Re-rasterising every page is costly, so a run of zoom steps settles first.
const RENDER_DEBOUNCE_MS = 150;

const props = defineProps({
  url: {
    type: String,
    required: true,
  },
  scale: {
    type: Number,
    default: 0.45,
  },
  allowDownload: {
    type: Boolean,
    default: true,
  },
  dataTestid: {
    type: String,
    default: "my-students-submission-current-document-pdf",
  },
});

const emit = defineEmits(["error"]);

const { url, scale, allowDownload, dataTestid } = toRefs(props);

const canvasViewer = ref();
const isPDFLoaded = ref(false);

let pdfDocument;
let renderTasks = [];
// Distinguishes the current render from one the scale changed out from under.
let renderGeneration = 0;

const cancelRendering = () => {
  renderTasks.forEach((task) => task.cancel());
  renderTasks = [];
};

const renderPages = async () => {
  const generation = ++renderGeneration;
  cancelRendering();

  if (!pdfDocument || !canvasViewer.value) {
    return;
  }
  canvasViewer.value.innerHTML = "";

  for (let pageNumber = 1; pageNumber <= pdfDocument.numPages; pageNumber++) {
    const page = await pdfDocument.getPage(pageNumber);
    if (generation !== renderGeneration) {
      return;
    }

    const viewport = page.getViewport({ scale: scale.value });
    const canvas = document.createElement("canvas");
    canvas.height = viewport.height;
    canvas.width = viewport.width;
    // Each page is its own sheet against the body's backdrop; carrying this on the
    // container instead would run the pages together as one continuous document.
    canvas.className = "bg-white shadow-lg";
    canvasViewer.value.appendChild(canvas);

    const task = page.render({ canvasContext: canvas.getContext("2d"), viewport });
    renderTasks.push(task);
    // Cancelling rejects the task, which is the ordinary path when the scale
    // changes mid-render and must not reach the error handler.
    task.promise
      .then(() => {
        isPDFLoaded.value = true;
      })
      .catch(() => {});
  }
};

const debouncedRenderPages = debounce(renderPages, RENDER_DEBOUNCE_MS);

const loadPDF = async () => {
  if (!url.value) {
    return;
  }
  isPDFLoaded.value = false;
  try {
    pdfDocument = await pdfjsLib.getDocument(url.value).promise;
    await renderPages();
  } catch (reason) {
    // PDF loading error
    Sentry.captureException(reason, {
      tags: {
        troubleshooting_note: "Failed to load pdf file on submission for My Students",
      },
    });
    // A signed URL that cannot be parsed is still an unreadable file, so the
    // caller is told rather than left showing a loader that never resolves.
    emit("error");
  }
};

watch(url, loadPDF);
watch(scale, debouncedRenderPages);

onMounted(loadPDF);

onBeforeUnmount(() => {
  debouncedRenderPages.cancel();
  cancelRendering();
});
</script>

<template>
  <div v-if="url" :data-testid="dataTestid">
    <SkeletonLoader
      v-if="!isPDFLoaded"
      height="h-24"
      data-testid="my-students-submission-current-document-pdf-loader"
    />
    <a
      v-if="allowDownload"
      ref="canvasViewer"
      :href="url"
      target="_blank"
      rel="noopener noreferrer"
      class="block"
      >&nbsp;</a
    >
    <div v-else ref="canvasViewer" class="flex flex-col items-center gap-4"></div>
  </div>
</template>
